import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  Tooltip,
  Typography
} from '@mui/material';
import { BookmarkAdd, Check, Close, OpenInFull } from '@mui/icons-material';
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { mainColors } from '../../theme/themeConfig';
import { saveChartApi } from '../../api/abe';
import Api from '../../api';
import useAbeStore from '../../store/zustand/abe';

interface AbeChartProps {
  compact?: boolean;
  data: any;
  title?: string;
}

const CURRENCY_NAME_PATTERN =
  /revenue|amount|total|cost|price|invoice|payment|balance|earning|profit|margin|value/i;

const isCurrencySeries = (
  seriesName: string,
  seriesIndex: number,
  seriesFormats: string[]
): boolean => {
  if (seriesFormats && seriesFormats[seriesIndex] !== undefined) {
    return seriesFormats[seriesIndex] === 'currency';
  }
  return CURRENCY_NAME_PATTERN.test(seriesName);
};

const formatValue = (val: number, currency: boolean): string => {
  if (currency) return `$${val.toLocaleString()}`;
  return val.toLocaleString();
};

const getSaveLabel = (saved: boolean, saving: boolean): string => {
  if (saved) return 'Saved!';
  if (saving) return 'Saving…';
  return 'Save to Dashboard';
};

const buildOptions = (data: any, compact: boolean): ApexOptions => {
  const seriesFormats: string[] = data.seriesFormats ?? [];
  const series: any[] = data.series ?? [];

  const tooltipY = series.map((s: any, i: number) => ({
    formatter: (val: number) =>
      formatValue(val, isCurrencySeries(s.name ?? '', i, seriesFormats))
  }));

  const firstCurrencyIndex = series.findIndex((s: any, i: number) =>
    isCurrencySeries(s.name ?? '', i, seriesFormats)
  );

  return {
    chart: {
      animations: { enabled: true, speed: 400 },
      background: 'transparent',
      toolbar: { show: !compact },
      type: data.chartType ?? 'line',
      zoom: { enabled: !compact }
    },
    colors: [
      mainColors.MAIN,
      mainColors.MAIN_2,
      mainColors.MAIN_3,
      '#4CAF50',
      '#FF9800'
    ],
    dataLabels: { enabled: false },
    grid: { borderColor: '#e0e0e0', strokeDashArray: 4 },
    legend: { position: 'bottom', show: !compact },
    plotOptions: {
      bar: { borderRadius: 4, horizontal: data.horizontal ?? false }
    },
    stroke: { curve: 'smooth', width: compact ? 2 : 3 },
    tooltip: { intersect: false, shared: true, y: tooltipY },
    xaxis: {
      categories: data.categories ?? [],
      labels: { style: { fontSize: compact ? '10px' : '12px' } }
    },
    yaxis: {
      labels: {
        formatter: (val: number) => formatValue(val, firstCurrencyIndex >= 0),
        style: { fontSize: compact ? '10px' : '12px' }
      }
    }
  };
};

function AbeChart({ compact, data, title }: AbeChartProps): JSX.Element | null {
  const [modalOpen, setModalOpen] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedDashboardId, setSelectedDashboardId] = useState<string>('');
  const [dashboards, setDashboards] = useState<any[]>([]);
  const { savedCharts, setSavedCharts } = useAbeStore();

  if (!data || !data.series) return null;

  const chartTitle = title ?? data.title ?? 'Chart';

  // Load dashboards when modal opens
  useEffect(() => {
    if (!modalOpen || dashboards.length > 0) return;
    Api.AdvancedDashboardApi.getAdvancedDashboards()
      .then((res: any) => {
        const list = res?.data ?? [];
        setDashboards(list);
        if (list.length > 0 && !selectedDashboardId) {
          setSelectedDashboardId(list[0].id);
        }
      })
      .catch(() => {});
  }, [modalOpen]);

  const handleSave = async () => {
    if (saving || saved || !selectedDashboardId) return;
    setSaving(true);
    try {
      const newChart = await saveChartApi(
        chartTitle,
        data,
        data.sql ?? undefined,
        selectedDashboardId
      );
      setSavedCharts([newChart, ...savedCharts]);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch {
      // Non-critical
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {/* Inline preview */}
      <Box
        onClick={() => compact && setModalOpen(true)}
        sx={{
          '&:hover .expand-btn': { opacity: 1 },
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          cursor: compact ? 'pointer' : 'default',
          p: 1,
          position: 'relative'
        }}
      >
        {chartTitle && (
          <Typography
            sx={{ color: 'text.secondary', display: 'block', px: 1 }}
            variant="caption"
          >
            {chartTitle}
          </Typography>
        )}
        <Chart
          height={compact ? 180 : 380}
          options={buildOptions(data, compact ?? false)}
          series={data.series}
          type={data.chartType ?? 'line'}
        />
        {compact && (
          <Tooltip title="Expand chart">
            <IconButton
              className="expand-btn"
              onClick={(e) => {
                e.stopPropagation();
                setModalOpen(true);
              }}
              size="small"
              sx={{
                bgcolor: 'rgba(255,255,255,0.9)',
                opacity: 0,
                position: 'absolute',
                right: 6,
                top: 6,
                transition: 'opacity 0.2s'
              }}
            >
              <OpenInFull fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Full modal */}
      <Dialog
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
        maxWidth="lg"
        onClose={() => setModalOpen(false)}
        open={modalOpen}
      >
        <DialogTitle
          sx={{
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'space-between'
          }}
        >
          <Typography sx={{ fontWeight: 600 }} variant="h6">
            {chartTitle}
          </Typography>
          <IconButton onClick={() => setModalOpen(false)} size="small">
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Chart
            height={420}
            options={buildOptions(data, false)}
            series={data.series}
            type={data.chartType ?? 'line'}
          />
        </DialogContent>

        <DialogActions
          sx={{
            borderColor: 'divider',
            borderTop: '1px solid',
            gap: 1.5,
            px: 3,
            py: 1.5
          }}
        >
          {dashboards.length > 0 && (
            <Select
              onChange={(e) => setSelectedDashboardId(e.target.value)}
              size="small"
              sx={{ minWidth: 180 }}
              value={selectedDashboardId}
            >
              {dashboards.map((d: any) => (
                <MenuItem key={d.id} value={d.id}>
                  {d.name}
                </MenuItem>
              ))}
            </Select>
          )}
          <Box sx={{ flex: 1 }} />
          <Button
            onClick={() => setModalOpen(false)}
            size="small"
            variant="text"
          >
            Close
          </Button>
          <Button
            disabled={saved || saving || !selectedDashboardId}
            onClick={handleSave}
            size="small"
            startIcon={saved ? <Check /> : <BookmarkAdd />}
            sx={{
              '&.Mui-disabled': {
                bgcolor: saved ? 'success.main' : undefined,
                color: saved ? '#fff' : undefined
              },
              '&:hover': {
                bgcolor: saved ? 'success.dark' : mainColors.MAIN_2
              },
              bgcolor: saved ? 'success.main' : mainColors.MAIN,
              color: '#fff',
              transition: 'background-color 0.3s'
            }}
            variant="contained"
          >
            {getSaveLabel(saved, saving)}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

AbeChart.defaultProps = {
  compact: false,
  title: undefined
};

export default AbeChart;
